import * as Papa from "papaparse";
import { useCallback, useMemo, useRef, useState } from "react";

export type ImportState =
  | {
      state: "idle";
    }
  | {
      state: "parsing";
    }
  | {
      state: "running" | "complete";

      rowCount: number;
      importCount: number;
      errorCount: number;

      // The remaining time in milliseconds
      remainingTime: number | null;
    }
  | {
      state: "error";

      error: Error;
    };

type usePapaParseProps<T> = {
  // The import batch size
  batchSize?: number;

  // Columns to keep as text, because a leading zero is meaningful there
  textColumns?: string[];

  // Field name of a column header (e.g. the French label of the template)
  mapHeader?: (header: string) => string;

  // Reads the rows of an Excel file, keyed by header
  readSpreadsheet?: (file: File) => Promise<Record<string, unknown>[]>;

  // processBatch returns the number of imported items
  processBatch(batch: T[]): Promise<number>;
};

const isExcelFile = (file: File) => /\.xlsx$/i.test(file.name);

/**
 * Imports a CSV or an Excel (.xlsx) file in batches. Both formats go through
 * the same header mapping and batch loop.
 */
export function usePapaParse<T>({
  batchSize = 10,
  textColumns,
  mapHeader,
  readSpreadsheet,
  processBatch,
}: usePapaParseProps<T>) {
  const importIdRef = useRef<number>(0);

  const [importer, setImporter] = useState<ImportState>({
    state: "idle",
  });

  const reset = useCallback(() => {
    setImporter({
      state: "idle",
    });
    importIdRef.current += 1;
  }, []);

  const importRows = useCallback(
    async (rows: T[], parseErrorCount: number, importId: number) => {
      setImporter({
        state: "running",
        rowCount: rows.length,
        errorCount: parseErrorCount,
        importCount: 0,
        remainingTime: null,
      });

      let totalTime = 0;
      for (let i = 0; i < rows.length; i += batchSize) {
        if (importIdRef.current !== importId) {
          return;
        }

        const batch = rows.slice(i, i + batchSize);
        try {
          const start = Date.now();
          // A batch reports how many of its rows the backend accepted, so
          // the rows it refused count as errors while the records that did
          // land are not reported as failures.
          const created = await processBatch(batch);
          totalTime += Date.now() - start;

          const meanTime = totalTime / (i + batch.length);
          setImporter((previous) => {
            if (previous.state === "running") {
              const importCount = previous.importCount + created;
              const errorCount = previous.errorCount + (batch.length - created);
              return {
                ...previous,
                importCount,
                errorCount,
                remainingTime:
                  meanTime * (rows.length - importCount - errorCount),
              };
            }
            return previous;
          });
        } catch (error) {
          console.error("Failed to import batch", error);
          setImporter((previous) =>
            previous.state === "running"
              ? {
                  ...previous,
                  errorCount: previous.errorCount + batch.length,
                }
              : previous,
          );
        }
      }

      setImporter((previous) =>
        previous.state === "running"
          ? {
              ...previous,
              state: "complete",
              remainingTime: null,
            }
          : previous,
      );
    },
    [batchSize, processBatch],
  );

  const parseCsv = useCallback(
    (file: File) => {
      setImporter({
        state: "parsing",
      });

      const importId = importIdRef.current;
      const fail = (error: Error) => {
        console.error(error);
        setImporter({ state: "error", error });
      };

      if (isExcelFile(file) && readSpreadsheet) {
        readSpreadsheet(file)
          .then((rows) => {
            if (importIdRef.current !== importId) return;
            const mapped = rows.map((row) =>
              Object.fromEntries(
                Object.entries(row).map(([header, value]) => [
                  mapHeader ? mapHeader(header) : header,
                  value,
                ]),
              ),
            );
            return importRows(mapped as T[], 0, importId);
          })
          .catch(fail);
        return;
      }

      Papa.parse<T>(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => (mapHeader ? mapHeader(header) : header),
        async complete(results) {
          if (importIdRef.current !== importId) {
            return;
          }
          await importRows(results.data, results.errors.length, importId);
        },
        error: fail,
        // Numeric-looking cells are converted, except where a leading zero
        // carries meaning: a "02134" zipcode would be stored as 2134.
        dynamicTyping: (field) => !textColumns?.includes(String(field)),
      });
    },
    [importRows, mapHeader, readSpreadsheet, textColumns],
  );

  return useMemo(
    () => ({
      importer,
      parseCsv,
      reset,
    }),
    [importer, parseCsv, reset],
  );
}
